import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import Select from 'react-select';
import {updateCondition, deleteCondition} from '../hooks/ruleUtils';
import {trashDarkRedTwo} from 'assets/images/images';
import {customStyles, DropdownIndicator} from '../../../../../components';
import {findParentByChild, getOperators} from '../../EditProcess/utils';
import {useFlowData} from '../../EditProcess/hooks/useFlowData';
import {NestedDropdown} from '../components/NestedDropDown';
import {ConditionValueInput} from '../components/ConditionValueInputs';
import {EasyDetectContext} from '../../../../../helpers/context/EasyDetectContext';

function Condition({
  ruleId,
  groupId,
  condition,
  rules,
  setRules,
  conditionType,
  isLoading,
}) {
  const currentRule = rules.find(rule => rule.id === ruleId);
  const currentGroup = useMemo(
    () => currentRule?.groups.find(group => group.id === groupId),
    [currentRule, groupId],
  );
  const canDelete = currentGroup?.conditions.length > 1;
  const FLowData = useFlowData();
  const {setAggregateFunction, setIsAggregateFunction, isAggregateFunction} =
    useContext(EasyDetectContext);
  const availableFields = React.useMemo(() => {
    if (!isLoading && FLowData.fieldsAndOperators) {
      return FLowData.fieldsAndOperators.data;
    }
  }, [FLowData.fieldsAndOperators, isLoading]);

  const findParentFields = useCallback(
    childValue => findParentByChild(childValue, availableFields),
    [availableFields],
  );

  const cleanField = useMemo(
    () => condition?.field.replace(/_last.*$/, ''),
    [condition?.field],
  );

  const OPERATOR_FIELD = useMemo(
    () =>
      getOperators(
        condition.parent || findParentFields(cleanField)?.value,
        cleanField,
        availableFields,
      ),
    [availableFields, cleanField, condition.parent, findParentFields],
  );

  const currentOperator = useMemo(
    () => OPERATOR_FIELD?.find(ops => ops.value === condition?.operator),
    [OPERATOR_FIELD, condition?.operator],
  );

  const handleUpdateCondition = useCallback(
    (key, value) => {
      const updatedRules = updateCondition(
        rules,
        ruleId,
        groupId,
        condition.id,
        key,
        value,
        isAggregateFunction,
      );
      setRules(updatedRules);
    },
    [rules, ruleId, groupId, condition.id, setRules, isAggregateFunction],
  );

  useEffect(() => {
    const currentActive = {...currentRule};
    let hasAggregateCondition = false;

    if (currentRule && Array.isArray(currentActive.groups)) {
      hasAggregateCondition = currentRule.groups.some(group => {
        if (group && Array.isArray(group.conditions)) {
          return group.conditions.some(cond => {
            const parentField = findParentFields(
              cond.field.replace(/_last.*$/, ''),
            )?.value;
            return parentField === 'aggregate_functions';
          });
        }
        return false;
      });
    }
    setIsAggregateFunction(hasAggregateCondition);
    setAggregateFunction(currentRule.event?.params?.period || 'last_30m');
  }, [
    setIsAggregateFunction,
    currentRule,
    findParentFields,
    setAggregateFunction,
  ]);

  const handleDeleteCondition = useCallback(() => {
    deleteCondition(rules, setRules, ruleId, groupId, condition.id);
  }, [rules, setRules, ruleId, groupId, condition.id]);

  const handleOperatorChange = useCallback(
    selectedOption => {
      handleUpdateCondition('operator', selectedOption?.value || '');
    },
    [handleUpdateCondition],
  );

  return (
    <div
      className={`${
        conditionType === 'or' ? 'flex-1 min-w-[300px]' : 'w-full'
      } border-b border-grey-100 pb-2 mb-3 last:border-none bg-white p-3 rounded-md relative`}
    >
      <div className="relative w-full flex-col flex ">
        {canDelete && (
          <button
            onClick={handleDeleteCondition}
            className="  absolute  z-20 right-3"
            title="Delete condition"
          >
            <img
              src={trashDarkRedTwo}
              className="min-w-4 min-h-4"
              alt="Delete"
            />
          </button>
        )}
        <>
          <label className="block mb-2">Fields</label>

          <NestedDropdown
            isLoading={isLoading}
            handleUpdateCondition={handleUpdateCondition}
            condition={condition}
            cleanField={cleanField}
          />
        </>
      </div>

      {/*{isAggregateFunction && (*/}
      {/*  <TimePeriod onChange={handleUpdateCondition} condition={condition} />*/}
      {/*)}*/}

      <div className="flex gap-2 mb-3 w-full mt-5">
        <div className="flex  flex-col gap-3 w-1/2">
          <div>
            <label className="block mb-1">Operator</label>
            <Select
              options={OPERATOR_FIELD}
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              className="basic-multi-select white-select capitalize"
              styles={customStyles}
              value={currentOperator}
              onChange={handleOperatorChange}
              placeholder="Select"
              isClearable
            />
          </div>
        </div>

        <div className="flex flex-col  w-1/2 relative">
          <label className="block mb-1">Value</label>
          <ConditionValueInput
            condition={condition}
            operator={currentOperator}
            onValueChange={handleUpdateCondition}
            ruleId={currentRule?.ruleId}
            updateData={{rules, groupId}}
          />
        </div>
      </div>
    </div>
  );
}

export default Condition;
